import { useState } from 'react'
import { Brand } from '../../../shared/components/Brand'

// Cryptic landing/consent — parallels the 24 ConsentScreen (same voice + opt-in consent
// structure and privacy link), with cryptic-specific objective copy and a static example
// clue in place of the 24 animated demo. Heading / sub-copy / example come from the variant
// (standard vs the easier arm) so the one component serves both cryptic games.
export const CrypticConsent = ({
  onConsent,
  heading = 'Crack the cryptic clue',
  sub = 'Each clue hides its answer in wordplay. The number in brackets is the answer’s length.',
  example = { clue: 'Bird’s home base? (4)', answer: 'NEST' },
}: {
  onConsent: () => void
  heading?: string
  sub?: string
  example?: { clue: string; answer: string }
}) => {
  const [checked, setChecked] = useState(false)
  const privacyHref =
    typeof window !== 'undefined'
      ? window.location.pathname.replace(/\/[^/]*$/, '') + '/privacy.html'
      : 'privacy.html'

  return (
    <div className="flex flex-col items-center px-6 pt-10 pb-6">
      <div className="w-full max-w-lg text-center">
        <Brand game="cryptic" />

        {/* ---- The game ---- */}
        <h1 className="mt-8 text-xl font-semibold text-gray-800">{heading}</h1>
        <p className="mt-1 text-sm text-gray-500">{sub}</p>

        <div className="mt-6 rounded-xl bg-[#820458] px-5 py-6 text-white shadow">
          <div className="text-lg font-semibold leading-snug">{example.clue}</div>
          <div className="mt-3 text-sm opacity-80">
            answer: <span className="font-semibold tracking-wide">{example.answer}</span>
          </div>
        </div>

        {/* ---- How nerdle labs games work ---- */}
        <h2 className="mt-10 text-base font-semibold text-gray-800">How to play nerdle labs games</h2>
        <p className="mt-2 text-gray-600 leading-relaxed">
          Every nerdle labs game is voice-activated — talk out loud as you work the clue through, so we can
          see how you think. Keep talking to reveal the clue, and say <span className="font-medium">“commit”</span>{' '}
          (or tap the button) when you’ve got it.
        </p>

        {/* ---- Consent ---- */}
        <p className="mt-4 text-xs text-gray-400">
          We record your voice as text only — your browser transcribes it; no audio is kept.
        </p>

        <label className="mt-5 flex items-start gap-3 cursor-pointer rounded-lg border border-gray-200 p-4 text-left hover:bg-gray-50">
          <input
            type="checkbox"
            className="mt-1 h-5 w-5 accent-[#820458]"
            checked={checked}
            onChange={(e) => setChecked(e.target.checked)}
          />
          <span className="text-sm text-gray-700">
            I confirm I am 18 or over and consent to my voice being recorded, transcribed to text, and the resulting transcripts being
            used for gaming and research purposes. I understand no audio is retained.{' '}
            <a
              href={privacyHref}
              target="_blank"
              rel="noopener noreferrer"
              onClick={(e) => e.stopPropagation()}
              className="underline hover:text-[#820458]"
            >
              More details.
            </a>
          </span>
        </label>

        <button
          type="button"
          disabled={!checked}
          onClick={onConsent}
          className="mt-5 w-full rounded-lg bg-[#820458] py-3 text-white font-semibold disabled:opacity-40 disabled:cursor-not-allowed"
        >
          Start
        </button>
        <p className="mt-2 text-xs text-gray-400">Your browser will ask for microphone access next.</p>
        <p className="mt-3 text-xs text-gray-400">
          <a href={privacyHref} className="underline hover:text-[#820458]">
            Privacy policy
          </a>
        </p>
      </div>
    </div>
  )
}
